import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Volume2, VolumeX, Bell, BellOff } from "lucide-react";
import { playAlarm, urlB64ToUint8Array } from "@/pages/AdminAgendaEquipe";
import { SeedMark } from "../SeedMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { admin: "Admin", corretor: "Corretor", administrativo: "Adm" };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` });

export const AdminNav = ({ user, onLogout }) => {
  const location = useLocation();
  const alarmedRef = useRef(new Set());
  const [muted, setMuted] = useState(() => localStorage.getItem("seed_alarm_muted") === "1");
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const isAdmin = user?.role === "admin";
  const isAgenda = isAdmin || user?.role === "administrativo";

  // Alarme sonoro de compromissos: roda em qualquer tela do painel (admin/administrativo)
  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "administrativo")) return;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/agenda/appointments`, { headers: authHeaders() });
        const now = Date.now();
        (r.data || [])
          .filter((a) => a.assignee === user.email && !a.done)
          .forEach((a) => {
            const due = new Date(a.date).getTime();
            const remindAt = due - (a.remind_minutes || 0) * 60000;
            const key = `${a.id}:${a.date}:${a.remind_minutes}`;
            if (now >= remindAt && !alarmedRef.current.has(key)) {
              alarmedRef.current.add(key);
              if (localStorage.getItem("seed_alarm_muted") !== "1") playAlarm();
              const when = new Date(a.date).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
              toast.warning(`⏰ Lembrete: ${a.title} — ${when}`, {
                duration: 60000,
                action: { label: "Ciente", onClick: () => {} },
              });
            }
          });
      } catch {
        /* sem acesso ou offline — tenta no próximo ciclo */
      }
    };
    check();
    const interval = setInterval(check, 20000);
    return () => clearInterval(interval);
  }, [user]);

  // Estado do push neste aparelho
  useEffect(() => {
    if (!isAgenda || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setPushOn(!!sub))
      .catch(() => {});
  }, [isAgenda]);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem("seed_alarm_muted", next ? "1" : "0");
      toast.success(next ? "Som dos lembretes silenciado. Toque de novo para reativar." : "Som dos lembretes reativado.");
      return next;
    });
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API}/push/unsubscribe`, { subscription: sub.toJSON() }, { headers: authHeaders() });
        await sub.unsubscribe();
        setPushOn(false);
        toast.success("Push desativado neste aparelho. Toque de novo para reativar.");
      } else {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          toast.error("Permita as notificações nas configurações do navegador.");
          return;
        }
        const { data } = await axios.get(`${API}/push/vapid-public`);
        const newSub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.public_key) });
        await axios.post(`${API}/push/subscribe`, { subscription: newSub.toJSON() }, { headers: authHeaders() });
        setPushOn(true);
        toast.success("Push ativado! Os lembretes chegam neste aparelho mesmo com a página fechada.");
      }
    } catch {
      toast.error("Não foi possível alternar o push neste aparelho.");
    } finally {
      setPushBusy(false);
    }
  };

  const operacao = [
    { to: "/admin", label: "Imóveis", testId: "admin-tab-imoveis" },
    { to: "/admin/crm", label: "CRM", testId: "admin-tab-crm" },
    { to: "/admin/agenda", label: "Agenda", testId: "admin-tab-agenda" },
    { to: "/admin/app", label: "App", testId: "admin-tab-app" },
  ];
  const gestao = [
    { to: "/admin/dashboard", label: "Dashboard", testId: "admin-tab-dashboard" },
    ...(isAdmin ? [{ to: "/admin/financeiro", label: "Financeiro", testId: "admin-tab-financeiro" }] : []),
  ];
  const administracao = [
    ...(isAdmin
      ? [
          { to: "/admin/site", label: "Site", testId: "admin-tab-site" },
          { to: "/admin/equipe", label: "Equipe", testId: "admin-tab-equipe" },
        ]
      : []),
    ...(isAgenda ? [{ to: "/admin/agenda-equipe", label: "Agenda equipe", testId: "admin-tab-agenda-equipe" }] : []),
  ];

  const groups = [];
  if (isAdmin || user?.role === "corretor") {
    groups.push({ label: "Operação", items: operacao }, { label: "Gestão", items: gestao });
  }
  if (administracao.length > 0) groups.push({ label: "Administração", items: administracao });

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-seed-deep/10 bg-seed-off/90 backdrop-blur-md" data-testid="admin-nav">
      <div className="mx-auto flex max-w-[1600px] items-center gap-3 overflow-x-auto px-4 py-2.5 lg:px-8">
        <Link to="/admin" className="flex shrink-0 items-center gap-2" data-testid="admin-nav-brand" aria-label="Ir para o início do painel">
          <SeedMark size={26} />
          <span className="hidden font-heading text-sm font-black tracking-tight text-seed-deep sm:inline">Painel</span>
        </Link>
        <nav className="flex items-center gap-2" aria-label="Menu do painel">
          {groups.map((g, gi) => (
            <div key={g.label} className="flex items-center gap-1.5">
              {gi > 0 && <span className="mx-1 h-5 w-px shrink-0 bg-seed-deep/10" />}
              <span className="hidden shrink-0 text-[9px] font-bold uppercase tracking-widest text-seed-deep/35 lg:inline">{g.label}</span>
              <div className="flex gap-0.5 rounded-full bg-white p-1 shadow-sm">
                {g.items.map((t) => {
                  const active = location.pathname === t.to;
                  return (
                    <Link
                      key={t.to}
                      to={t.to}
                      data-testid={t.testId}
                      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-heading text-xs font-bold transition-all duration-300 ${
                        active ? "bg-seed-salad text-white shadow-md" : "text-seed-deep/60 hover:text-seed-deep"
                      }`}
                    >
                      {t.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {isAgenda && (
            <>
              <button
                onClick={toggleMute}
                aria-label={muted ? "Reativar som dos lembretes" : "Silenciar som dos lembretes"}
                title={muted ? "Som dos lembretes: mudo (toque para reativar)" : "Som dos lembretes: ativo (toque para silenciar)"}
                data-testid="admin-alarm-toggle"
                className={`flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm transition-colors ${muted ? "text-red-400" : "text-seed-deep/60 hover:text-seed-salad"}`}
              >
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
              <button
                onClick={togglePush}
                disabled={pushBusy}
                aria-label={pushOn ? "Desativar notificações push neste aparelho" : "Ativar notificações push neste aparelho"}
                title={pushOn ? "Push: ativo neste aparelho (toque para desativar)" : "Push: desativado (toque para ativar)"}
                data-testid="admin-push-toggle"
                className={`flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm transition-colors disabled:opacity-50 ${pushOn ? "text-seed-salad" : "text-seed-deep/60 hover:text-seed-salad"}`}
              >
                {pushOn ? <Bell size={15} /> : <BellOff size={15} />}
              </button>
            </>
          )}
          {user && (
            <span className="hidden items-center text-xs font-semibold text-seed-deep/60 md:flex" data-testid="admin-user-name">
              {user.name || user.email}
              <span className="ml-1.5 rounded-full bg-seed-salad/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-seed-salad">
                {ROLE_LABELS[user.role] || user.role}
              </span>
            </span>
          )}
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="flex items-center gap-1.5 rounded-full border border-seed-deep/20 px-4 py-2 font-heading text-xs font-bold text-seed-deep transition-colors hover:border-red-400 hover:text-red-500"
          >
            <LogOut size={13} /> Sair
          </button>
        </div>
      </div>
    </header>
  );
};
